import { ChangeEvent, useContext, useEffect, useState } from "react";

import * as axios from "axios";

import { Spinner } from "@salesforce/design-system-react";

import { Button, Notification } from "@vimeo/iris/components";

import { ListResponse, Thumbnail, Video } from "vimeo";
import {
    getVideoIdFromUri,
    openCreateThumbnailWidget,
    appOrigin,
} from "../common";

import EmbedInEmailContext, {
    EmbedInEmailContextState,
} from "../contexts/embedInEmailContext";
import VideoSettingsContext from "../contexts/videoSettingsContext";

import copy from "copy-to-clipboard";

import { client as vimeoClient, getVideo } from "../vimeoClient";
import { EmbedInEmail } from "./EmbedInEmail/EmbedInEmail";

import "./settings.scoped.scss";

import { VerticalDivider } from "../components/VerticalDivider";
import { VimeoPlayer } from "./VimeoPlayer";
import { ConfirmModal } from "../components/ConfirmModal";
import { ThumbnailCard } from "../components/ThumbnailCard";
import { FileUpload } from "../components/FileUpload";
import {
    EmbedInEmailSource,
    EventName,
    getSfmcLocation,
    sendAnalyticsEvent,
} from "../analytics/common";

export function ThumbnailsSettings() {
    const [embedInEmailContextState, setEmbedInEmailContextState] = useState<
        Partial<EmbedInEmailContextState>
    >({});
    const [videoSettingsContext, setVideoSettingsContext] =
        useContext(VideoSettingsContext);

    const [thumbnails, setThumbnails] = useState<Thumbnail[]>([]);
    const [loading, setLoading] = useState(false);
    const [showEmbedInEmailModal, setShowEmbedInEmailModal] = useState(false);
    const [video, setVideo] = useState<Video>();
    const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
    const [thumbnailToDelete, setThumbnailToDelete] = useState<string>();
    const [notificationContent, setNotificationContent] = useState("");

    useEffect(() => {
        setVideo(videoSettingsContext.video);
    }, [videoSettingsContext]);

    async function getThumbnails() {
        const resp = await vimeoClient.get<ListResponse<Thumbnail>>(
            `/api/vimeo${video?.uri}/pictures`
        );
        setThumbnails(resp.data.data);
    }

    useEffect(() => {
        if (!video) {
            return;
        }

        sendAnalyticsEvent(EventName.pageView, {
            name: "video_settings",
            location: getSfmcLocation(),
            video_id: getVideoIdFromUri(video.uri),
            section: "thumbnails",
        });

        setLoading(true);

        getThumbnails()
            .catch(console.error)
            .finally(() => setLoading(false));

        setEmbedInEmailContextState({
            videoId: getVideoIdFromUri(video.uri),
            video: video,
            link: video.link,
            name: video.name,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [video]);

    async function onVisibilityChange() {
        if (document.visibilityState !== "visible") {
            return;
        }

        window.document.removeEventListener(
            "visibilitychange",
            onVisibilityChange
        );

        setLoading(true);
        await getThumbnails();
        setLoading(false);
    }

    async function deleteThumbnail(uri: string) {
        await vimeoClient.delete(`/api/vimeo${uri}`);
    }

    function onDownloadOptionClicked(link: string) {
        window.open(link);
    }

    function onDeleteOptionClicked(uri: string) {
        setThumbnailToDelete(uri);
        setShowDeleteConfirmModal(true);
    }

    function onEmbedInEmailClicked(thumbnailUri: string) {
        const thumbnail = thumbnails.filter((t) => t.uri === thumbnailUri)[0];
        setEmbedInEmailContextState({
            ...embedInEmailContextState,
            imgUrl: thumbnail.sizes[6].link,
            imgUrlWithPlayButton: thumbnail.sizes[6].link_with_play_button,
            selectedImgType: "thumbnail",
            source: EmbedInEmailSource.videoSettings,
        });

        sendAnalyticsEvent(EventName.openEmbedInEmail, {
            path: null,
            is_my_videos: null,
            clip_id: embedInEmailContextState.videoId,
            location: "video_settings",
        }, 3);

        sendAnalyticsEvent(EventName.integrationsClick, {
            name: "select_asset_embed_in_email",
            location: embedInEmailContextState.source,
            video_id: embedInEmailContextState.videoId,
            asset_type: embedInEmailContextState.selectedImgType,
            cta: "next",
        });
        setShowEmbedInEmailModal(true);
    }

    async function onCreateThumbnailClicked() {
        const videoId = embedInEmailContextState.videoId;

        sendAnalyticsEvent(EventName.integrationsClick, {
            name: "open_thumbnail_modal",
            location: "video_settings",
            video_id: videoId,
            cta: "create_thumbnail",
        });

        const opened = await openCreateThumbnailWidget(videoId);

        window.document.addEventListener(
            "visibilitychange",
            onVisibilityChange
        );

        if (!opened) {
            return;
        }

        opened.onmessage = async (e: MessageEvent) => {
            if (e.origin !== appOrigin) {
                return;
            }

            await getThumbnails();
        };
    }

    async function onConfirmDelete() {
        if (!thumbnailToDelete) {
            return;
        }

        setShowDeleteConfirmModal(false);
        await deleteThumbnail(thumbnailToDelete);
        setThumbnailToDelete(undefined);

        // Refresh the thumbnails.
        setLoading(true);
        await getThumbnails();
        setLoading(false);
    }

    async function onThumbnailClicked(uri: string) {
        setLoading(true);

        await vimeoClient.patch(`/api/vimeo${uri}`, { active: true });

        if (!videoSettingsContext.video) {
            setLoading(false);
            return;
        }

        const videoId = getVideoIdFromUri(videoSettingsContext.video.uri);

        sendAnalyticsEvent(EventName.integrationsClick, {
            name: "change_thumbnail",
            video_id: videoId,
            location: "video_settings",
        });

        const v = await getVideo(videoId);
        setVideoSettingsContext({ ...videoSettingsContext, video: v });
        setLoading(false);
    }

    async function onFileSelected(e: ChangeEvent) {
        const files = (e.target as HTMLInputElement).files;
        if (!files || !files.length) {
            return;
        }

        const file = files[0];
        if (file.type !== "image/png" && file.type !== "image/jpg") {
            return;
        }

        setLoading(true);

        try {
            const formData = new FormData();
            formData.append("data", file);

            await axios.default.post(
                `/api/vimeo${video?.uri}/pictures?imageType=${
                    file.type
                }&picturesUri=${video!.metadata.connections.pictures.uri}`,
                formData,
                {
                    headers: {
                        Accept: "application/vnd.vimeo.*+json;version=3.4",
                        "Content-Length": file.size,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            await getThumbnails();
        } catch (err) {
            console.log("Failed to upload image", err);
            setNotificationContent(
                "Failed to upload image. Please try again later."
            );
        }

        setLoading(false);
    }

    return (
        <div>
            {loading && <Spinner />}
            <div className="slds-grid">
                <div
                    className="slds-col"
                    style={{ display: "flex", flexFlow: "column nowrap" }}
                >
                    <div className="slds-var-p-bottom_large">
                        {!videoSettingsContext.hideSettingsViewHeading && (
                            <div className="slds-text-heading_medium">
                                Thumbnails
                            </div>
                        )}
                    </div>

                    {thumbnails.map((t, index) => (
                        <ThumbnailCard
                            key={index}
                            active={t.active}
                            onClick={() => onThumbnailClicked(t.uri)}
                            baseLink={t.base_link}
                            imgLink={t.sizes[0].link}
                            thumbnailType="thumbnail"
                            thumbnailUri={t.uri}
                            onCopyOptionClicked={(baseLink) => {
                                copy(baseLink);
                                setNotificationContent("Copied!");
                            }}
                            onDeleteOptionClicked={onDeleteOptionClicked}
                            onDownloadOptionClicked={onDownloadOptionClicked}
                            onEmbedInEmailClicked={onEmbedInEmailClicked}
                        />
                    ))}

                    {!loading && !thumbnails?.length && (
                        <div>You have no thumbnails created.</div>
                    )}
                    <Button
                        className="slds-var-m-top_small"
                        format="primary"
                        onClick={onCreateThumbnailClicked}
                    >
                        Select from video
                    </Button>
                    <div className="slds-var-m-top_small">
                        <FileUpload
                            onChange={onFileSelected}
                            mimeTypes="image/png,image/jpg"
                        />
                    </div>
                </div>
                <div>
                    <VerticalDivider />
                </div>
                <div className="slds-col">
                    {video && <VimeoPlayer video={video} />}
                </div>
            </div>
            {embedInEmailContextState.video && (
                <EmbedInEmailContext.Provider
                    value={[
                        embedInEmailContextState,
                        setEmbedInEmailContextState,
                    ]}
                >
                    <EmbedInEmail
                        source={EmbedInEmailSource.videoSettings}
                        useModal={true}
                        show={showEmbedInEmailModal}
                        onClose={() => setShowEmbedInEmailModal(false)}
                        hideAssetSelection={true}
                    />
                </EmbedInEmailContext.Provider>
            )}

            <ConfirmModal
                active={showDeleteConfirmModal}
                onClose={() => setShowDeleteConfirmModal(false)}
                message="Deleting a thumbnail cannot be undone. Are you sure?"
                onCancelClicked={() => setShowDeleteConfirmModal(false)}
                onConfirmClicked={onConfirmDelete}
            />

            <Notification
                content={notificationContent}
                showing={notificationContent !== ""}
                automatic={true}
                onComplete={() => setNotificationContent("")}
            ></Notification>
        </div>
    );
}
